//REACT
import { Helmet } from "react-helmet";
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";

//HOOKS, HELPERS, SERVICES
import { useQuery } from "Hooks/index";
import useSession from "Hooks/session.hook";
import usePagination from "Hooks/pagination.hook";
import { decode } from "Helpers/enc";
import { toaster } from "Helpers/toaster";
import { fetchIncidentReport } from "Services/incident-report.services";

//STYLE
import "../incident-report.scss";
import { CloseCircleOutlined } from "@ant-design/icons";

//COMPONENTS
import { Row, Col } from "antd";
import ModuleTitleBar from "Components/shared/module-title-bar/module-title-bar";
import ErrorBoundary from "antd/lib/alert/ErrorBoundary";

const DetailsPanelFallback = () => {
  return (
    <Row>
      <Col span={24}>No information found.</Col>
    </Row>
  );
};

const DetailsPanel = ({ incidentReportDetails }) => {
  const { t } = useTranslation();

  return (
    <>
      <Col span={6}>
        <Row gutter={[0, 4]}>
          <Col span={24} className="label">
            {t("label.type")}
          </Col>
          <Col span={24}>
            {incidentReportDetails?.typeData == null
              ? incidentReportDetails?.type
              : incidentReportDetails?.typeData.map((types) => types?.name)}
          </Col>
        </Row>
      </Col>
      <Col span={6}>
        <Row gutter={[0, 4]}>
          <Col span={24} className="label">
            {t("label.info")}
          </Col>
          <Col span={24}>{incidentReportDetails?.info}</Col>
        </Row>
      </Col>
      <Col span={6}>
        <Row gutter={[0, 4]}>
          <Col span={24} className="label">
            {t("label.category")}
          </Col>
          <Col flex="auto">{incidentReportDetails?.category}</Col>
        </Row>
      </Col>
      <Col span={6}>
        <Row gutter={[0, 4]}>
          <Col span={24} className="label">
            {t("label.raisedBy")}
          </Col>
          <Col flex="auto">{incidentReportDetails?.profileData?.fullName}</Col>
        </Row>
      </Col>
      <Col span={24}>
        <Row gutter={[0, 4]}>
          <Col span={24} className="label">
            {t("label.remarks")}
          </Col>
          <Col span={24}>{incidentReportDetails?.remarks}</Col>
        </Row>
      </Col>
      {incidentReportDetails?.image && (
        <Row gutter={[0, 4]}>
          <Col span={24} className="label">
            {t("label.image")}
          </Col>
          <Col span={24}>
            <img
              src={`${incidentReportDetails.imageData.file_url}`}
              alt="incident-report"
              className="ir-image"
            />
          </Col>
        </Row>
      )}
    </>
  );
};

const IncidentReportDetailsPage = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const query = useQuery();
  const { id } = useParams<{ id: string }>();
  const decodedOrderID = decode(id);
  const [incidentReportDetails, setIncidentReportDetails] = useState<any>();

  const [isFallback, setIsFallback] = useState<boolean>(false);
  const statusType = incidentReportDetails?.status.toLowerCase();

  const onBack = () => {
    history.goBack();
  };

  useEffect(() => {
    fetchIncidentReportDetails(decodedOrderID);
  }, [id]);

  const fetchIncidentReportDetails = async (id) => {
    const responseData = await fetchIncidentReport(id);
    if (responseData && !responseData.error) {
      setIncidentReportDetails(responseData);
    } else {
      setIsFallback && setIsFallback(true);
      toaster("error", "Error fetching incident report");
    }
  };

  return (
    <ErrorBoundary>
      <div className="incident-report">
        <Helmet>
          <title>{t("nav.incidentReports")}</title>
        </Helmet>
        <ModuleTitleBar titleText="Incident Report" subtitleText="View Incident Report" />
        <div className="p-20 my-20 parcel-order-view-container">
          <div className="header-container">
            <Row>
              <Col flex="auto">
                <div className="main-header">
                  <div className="mh-title">
                    {!isFallback ? incidentReportDetails?.reportCode : "No data"}
                  </div>
                  <div className={`mh-status ${statusType}`}>
                    {!isFallback ? incidentReportDetails?.status : "Status unavailable"}
                  </div>
                </div>
              </Col>
              <Col flex="none" className="col-center">
                <CloseCircleOutlined
                  onClick={() => {
                    onBack();
                  }}
                  className="cursor-pointer back-action right"
                  style={{ fontSize: "20px" }}
                />
              </Col>
            </Row>
          </div>
          <div className={`order-details ${statusType}`}>
            <Row gutter={[0, 32]}>
              {isFallback ? (
                <DetailsPanelFallback />
              ) : (
                <DetailsPanel incidentReportDetails={incidentReportDetails} />
              )}
            </Row>
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default IncidentReportDetailsPage;
